import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Mail,
  ArrowRight,
  ArrowLeft,
  Shield,
  Clock,
  CheckCircle2,
  KeyRound,
  MailCheck,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  forgotPasswordApiV1AuthForgotPasswordPost,
  resetPasswordApiV1AuthResetPasswordPost,
} from "@/backend-sdk/client/sdk.gen";
import { isHeyApiError } from "@/lib/hey-api-client";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Campo obrigatório",
        description: "Por favor, informe seu e-mail",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      await forgotPasswordApiV1AuthForgotPasswordPost({
        body: { email },
        throwOnError: true,
      });

      setSubmitted(true);

      toast({
        title: "E-mail enviado",
        description: "Verifique sua caixa de entrada",
      });
    } catch (error) {
      const message =
        isHeyApiError(error) && typeof error.detail === "string"
          ? error.detail
          : "Erro ao enviar e-mail de recuperação";

      toast({
        title: "Erro",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || !password || !confirmPassword) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: "Senha fraca",
        description: "A senha deve ter pelo menos 8 caracteres",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Senhas não conferem",
        description: "A senha e a confirmação devem ser iguais",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      await resetPasswordApiV1AuthResetPasswordPost({
        body: {
          code,
          email,
          password,
          password_confirmation: confirmPassword,
        },
        throwOnError: true,
      });

      setResetDone(true);

      toast({
        title: "Senha redefinida",
        description: "Sua senha foi alterada com sucesso",
      });
    } catch (error) {
      const message =
        isHeyApiError(error) && typeof error.detail === "string"
          ? error.detail
          : "Erro ao redefinir senha";

      toast({
        title: "Erro",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Shield,
      title: "Recuperação Segura",
      description: "Processo protegido para garantir a segurança da sua conta.",
    },
    {
      icon: Clock,
      title: "Link Temporário",
      description: "O link de recuperação expira em 24 horas por segurança.",
    },
    {
      icon: KeyRound,
      title: "Nova Senha",
      description: "Crie uma senha forte para proteger suas informações.",
    },
  ];

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Section - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2869C1]/20 to-[#9CCB3B]/10" />

        {/* Content */}
        <div className="relative z-10 space-y-10">
          {/* Logo + Title */}
          <div className="space-y-6">
            <Link to="/login">
              <img
                src="/logoSidebar.png"
                alt="Painel do E-Commerce"
                className="h-16"
              />
            </Link>
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-white leading-tight">
                Esqueceu sua senha?
                <br />
                <span className="text-[#9CCB3B]">Não se preocupe</span>
              </h1>
              <p className="text-slate-400 text-lg max-w-md">
                Acontece com todo mundo. Vamos ajudá-lo a recuperar o acesso à
                sua conta de forma rápida e segura.
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
              >
                <div className="p-2 rounded-lg bg-[#9CCB3B]/20">
                  <feature.icon className="h-5 w-5 text-[#9CCB3B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-slate-400">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="text-slate-500 text-sm pt-8">
            © {new Date().getFullYear()} Painel do E-Commerce. Todos os direitos
            reservados.
          </div>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/login">
              <img
                src="/logoSidebar.png"
                alt="Painel do E-Commerce"
                className="h-20 mx-auto"
              />
            </Link>
          </div>

          {resetDone ? (
            <div className="text-center space-y-6">
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              </div>

              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-slate-900">
                  Senha redefinida
                </h2>
                <p className="text-slate-600 max-w-sm mx-auto">
                  Sua senha foi alterada com sucesso.
                </p>
              </div>

              <div className="pt-4">
                <Button
                  className="w-full h-12 text-base font-semibold bg-[#2869C1] hover:bg-[#1d5299]"
                  onClick={() => navigate("/login")}
                >
                  Ir para o login
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          ) : submitted ? (
            <div className="text-center space-y-6">
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <MailCheck className="h-10 w-10 text-emerald-600" />
              </div>

              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-slate-900">
                  Digite o código
                </h2>
                <p className="text-slate-600 max-w-sm mx-auto">
                  Enviamos um código de 6 dígitos para{" "}
                  <strong className="text-slate-900">{email}</strong>
                </p>
              </div>

              <form onSubmit={handleReset} className="space-y-4 text-left">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-slate-700">
                    Código de verificação
                  </Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    className="h-12 bg-white border-slate-200 text-center text-lg tracking-[0.5em] focus:border-[#2869C1] focus:ring-[#2869C1] text-slate-900"
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-slate-700">
                    Nova senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínimo 8 caracteres"
                      className="h-12 bg-white border-slate-200 focus:border-[#2869C1] focus:ring-[#2869C1] text-slate-900"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-slate-700">
                    Confirmar senha
                  </Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Repita a nova senha"
                    className="h-12 bg-white border-slate-200 focus:border-[#2869C1] focus:ring-[#2869C1] text-slate-900"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-[#2869C1] hover:bg-[#1d5299]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Redefinindo...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      Redefinir senha
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </Button>
              </form>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                <p>
                  Não encontrou o e-mail? Verifique sua pasta de spam ou lixo
                  eletrônico.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setCode("");
                    setPassword("");
                    setConfirmPassword("");
                  }}
                  variant="outline"
                  className="w-full h-12 border-slate-200"
                >
                  Tentar outro e-mail
                </Button>

                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="w-full h-12 text-slate-600"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Voltar ao login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            /* Form State */
            <>
              {/* Header */}
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#2869C1]/10 flex items-center justify-center mb-6">
                  <KeyRound className="h-8 w-8 text-[#2869C1]" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">
                  Recuperar senha
                </h2>
                <p className="mt-2 text-slate-600">
                  Digite seu e-mail para receber as instruções
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">
                    E-mail cadastrado
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-11 h-12 bg-white border-slate-200 focus:border-[#2869C1] focus:ring-[#2869C1] text-slate-900"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-[#2869C1] hover:bg-[#1d5299]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Enviando...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      Enviar instruções
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </Button>
              </form>

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar ao login
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-6 pt-4 text-sm text-slate-500">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  Processo seguro
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  Link temporário
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
